import { DOCUMENT, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Locale } from 'date-fns';
import { de, enGB, nb, sv } from 'date-fns/locale';
import { of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, filter, startWith } from 'rxjs/operators';
import { getLocalStorage } from '../utils/getLocalStorage';

/**
 * Wrapper for translate service holding current locale state
 */
@Injectable({ providedIn: 'root' })
export class LanguageService implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  subscriptions: Subscription[] = [];
  onLangChange = new ReplaySubject<LangChangeEvent>(1);

  private _dateLocale!: Locale;

  get dateLocale(): Locale {
    if (!this._dateLocale) this.setDefaults();
    return this._dateLocale;
  }

  get supportedLanguages() {
    return ['nb', 'se', 'en', 'de'];
  }

  /**
   * These should not be translated!
   */
  get supportedLanguageOptions() {
    return [
      { language: 'Norsk', code: 'nb' },
      { language: 'English', code: 'en' },
      { language: 'Svenska', code: 'se' },
      { language: 'Deutch', code: 'de' },
    ];
  }

  get current(): string {
    return this.translate.currentLang;
  }

  get currentOption(): { language: string; code: string } {
    const currentCode = this.current;

    return (
      this.supportedLanguageOptions.find((languageOption) => languageOption.code === currentCode) ||
      this.supportedLanguageOptions[0]
    );
  }

  get translations() {
    return this.translate.getTranslation(this.current);
  }

  constructor() {
    // this.onLangChange.next({ lang: this.translate.currentLang, translations: this.translate.translations });
    this.subscriptions.push(
      this.translate.onLangChange
        .pipe(
          startWith({ lang: this.translate.currentLang, translations: this.translate.translations }),
          filter((lng) => !!lng.lang),
        )
        .subscribe((lng) => {
          this.change(lng.lang);
          this.onLangChange.next(lng);
        }),
    );
  }

  getLocale(): string {
    return this.dateLocale?.code || '';
  }

  /**
   * Maps internal language codes to proper HTML lang attribute codes
   */
  private getHtmlLangCode(lang: string): string {
    switch (lang) {
      case 'no':
        return 'nb'; // Norwegian Bokmål
      case 'se':
        return 'sv'; // Swedish
      case 'en':
        return 'en'; // English
      case 'de':
        return 'de'; // German
      default:
        return 'en'; // Default fallback
    }
  }

  /**
   * Updates the HTML document lang attribute
   */
  private updateHtmlLangAttribute(lang: string): void {
    const htmlLangCode = this.getHtmlLangCode(lang);
    this.document.documentElement.lang = htmlLangCode;
  }

  /**
   * Set translation defaults
   * This will detect browser language and set this if nothing is previously stored
   * in localStorage. The configuration will be set back to localStorage once detected.
   */
  setDefaults() {
    this.translate.addLangs(this.supportedLanguages);
    // this.translate.setDefaultLang('no');
    const browserLang: string = navigator.language;
    const newLang = getLocalStorage().getItem('lang') || browserLang;
    this.change(newLang ? newLang : this.translate.getDefaultLang());
  }

  /**
   * Dynamically loads locale data for Angular's DatePipe, CurrencyPipe, etc.
   */
  private async loadLocaleData(locale: string, code: string): Promise<void> {
    try {
      switch (locale) {
        case 'nb': {
          const [localeNo, localeNoExtra] = await Promise.all([
            import('@angular/common/locales/nb'),
            import('@angular/common/locales/extra/nb'),
          ]);
          registerLocaleData(localeNo.default, code, localeNoExtra.default);
          break;
        }
        case 'sv': {
          const [localeSv, localeSvExtra] = await Promise.all([
            import('@angular/common/locales/sv'),
            import('@angular/common/locales/extra/sv'),
          ]);
          registerLocaleData(localeSv.default, code, localeSvExtra.default);
          break;
        }
        case 'de': {
          const [localeDe, localeDeExtra] = await Promise.all([
            import('@angular/common/locales/de'),
            import('@angular/common/locales/extra/de'),
          ]);
          registerLocaleData(localeDe.default, code, localeDeExtra.default);
          break;
        }
        case 'en-GB': {
          const [localeEn, localeEnExtra] = await Promise.all([
            import('@angular/common/locales/en-GB'),
            import('@angular/common/locales/extra/en-GB'),
          ]);
          registerLocaleData(localeEn.default, code, localeEnExtra.default);
          break;
        }
        default:
          console.warn(`Locale ${locale} not supported for dynamic loading`);
      }
    } catch (error) {
      console.error(`Failed to load locale data for ${locale}:`, error);
    }
  }

  /**
   * Change language with dynamic locale loading
   */
  async change(lang: string) {
    if (!lang) return;
    lang = lang.toLowerCase();

    if (lang.match(/no|nb|nn|no-nb/i)) {
      lang = 'no';
      this._dateLocale = nb;
      await this.loadLocaleData('nb', 'nb');
    } else if (lang.match(/se|sv|sv_se/i)) {
      lang = 'se';
      this._dateLocale = sv;
      await this.loadLocaleData('sv', 'sv');
    } else if (lang.match(/de/i)) {
      lang = 'de';
      this._dateLocale = de;
      await this.loadLocaleData('de', 'de');
    } else {
      // The default fallback
      lang = 'en';
      this._dateLocale = enGB;
      await this.loadLocaleData('en-GB', 'en-GB');
    }

    // Update the HTML lang attribute
    this.updateHtmlLangAttribute(lang);

    getLocalStorage().setItem('lang', lang);
    if (lang !== this.translate.currentLang) {
      return this.translate.use(lang);
    }
    this.getLocale();
    return of(lang);
  }

  loadTranslations(type: string) {
    return this.http
      .get<{ [key: string]: string }>(`/lang/${type}/${this.current}.json`, { headers: { NoLoad: 'true' } })
      .pipe(
        catchError((err) => {
          console.error('Failed to load translations:', err);
          return of({});
        }),
      );
  }

  ngOnDestroy() {
    this.subscriptions.filter((s) => !!s && !s.closed).forEach((s) => s.unsubscribe());
  }
}
